import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';

import { AttributablePay } from '@fora-monorepo/employer-my-pages-common/types';
import { stringToNumberOrNull, swedishNumberFormatter } from '@fora-monorepo/string-utils';
import {
  Alert,
  Assets,
  Box,
  Button,
  CssGrid,
  Feedback,
  Flex,
  ForaModal,
  Headings,
  InputField,
  Text,
} from '@foradevelopment/fora-ui';
import { getReadablePayMonth } from '@fora-monorepo/employer-my-pages-common/utils';

import { AttributablePayInputProps, AttributablePayModalProps, ModalContentProps, PayInputState } from './types';
import { initAttrPayInput, inputOnChangeFormatPay } from './utils';
import { AddButton, ChangeButton } from './ModalButtons';
import { isPayAmountSameOrLargerThanAttributableTotal } from '../../../common/utils';
import { useTypedTranslation } from '../../../i18n/i18n';

export const AttributablePayModal = ({
  hasAttributablePay,
  payAmountString,
  availableAttributablePayYears: availableAttributablePayYearsAndMonths,
  changeButtonFontSize,
  ...modalContentProps
}: AttributablePayModalProps) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  const openModal = () => setModalIsOpen(true);
  const closeModal = () => setModalIsOpen(false);
  const payAmount = useMemo(
    () => (payAmountString ? (stringToNumberOrNull(payAmountString) ?? 0) : 0),
    [payAmountString]
  );

  if (!payAmount && !hasAttributablePay) return null;

  return (
    <ForaModal
      button={
        hasAttributablePay ? (
          <ChangeButton openModal={openModal} fontSize={changeButtonFontSize} />
        ) : (
          <AddButton openModal={openModal} />
        )
      }
      isOpen={modalIsOpen}
      closeModal={closeModal}
    >
      <ModalContent
        {...modalContentProps}
        availableAttributablePayYears={availableAttributablePayYearsAndMonths}
        payAmount={payAmount}
        closeModal={closeModal}
      />
    </ForaModal>
  );
};

const ModalContent = ({
  firstName,
  lastName,
  availableAttributablePayYears,
  ...modalContentProps
}: ModalContentProps) => {
  const { t } = useTypedTranslation();
  const { attributablePay, payMonth, onSave, payAmount, closeModal } = modalContentProps;

  const [attrPayFormData, setAttrPayFormData] = useState<AttributablePay>(attributablePay ?? []);
  const [showTooHighAmountError, setShowTooHighAmountError] = useState(false);
  const [scrollToErrorMessages, setScrollToErrorMessages] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const errorMessageContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollToErrorMessages) {
      setScrollToErrorMessages(false);
      errorMessageContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'end' });
    }
  }, [scrollToErrorMessages]);

  const totalAttributableAmount = useMemo(
    () => attrPayFormData.reduce((total, pay) => total + pay.attributablePayAmount, 0),
    [attrPayFormData]
  );

  const onSaveClick = () => {
    let valid = true;
    if (!isPayAmountSameOrLargerThanAttributableTotal(payAmount, attrPayFormData)) {
      setShowTooHighAmountError(true);
      setScrollToErrorMessages(true);
      valid = false;
    }
    if (valid) {
      onSave(attrPayFormData);
      closeModal();
    }
  };

  const currentYear = availableAttributablePayYears[currentIndex];

  const handleNextClick = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handlePreviousClick = () => {
    if (currentIndex < availableAttributablePayYears.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  return (
    <>
      <ForaModal.Content hasCloseButton closeModal={closeModal}>
        <Headings.H3 marginBottom={20}>{t('modal.attributablePay.modalTitle')}</Headings.H3>
        <Text marginBottom={40}> {t('modal.attributablePay.modalInfoText')}</Text>

        <Text fontWeight="light" color="black70" marginBottom={6}>
          {t('modal.attributablePay.paidPayrollThisMonth', {
            payMonth: getReadablePayMonth(payMonth),
            firstName,
            lastName,
          })}
        </Text>
        <Headings.H2 marginBottom={20}>
          {t('modal.attributablePay.paidPayAmount', {
            payAmount: swedishNumberFormatter(payAmount),
          })}
        </Headings.H2>
        {currentYear && !currentYear.months.every(item => item.disabled === true) ? (
          <Box
            backgroundColor="blueHover"
            paddingBottom={40}
            paddingLeft={20}
            paddingRight={20}
            paddingTop={20}
            marginBottom={20}
            key={currentYear.year}
          >
            <CssGrid gridTemplateColumns={'repeat(3, 1fr)'}>
              <Box>
                {currentIndex !== availableAttributablePayYears.length - 1 && (
                  <Button
                    styleAs="link"
                    onClick={handlePreviousClick}
                    disabled={currentIndex === availableAttributablePayYears.length - 1}
                  >
                    <Assets.ChevronIcon direction="left" variant="thin" color={'foraBlue'} />
                    <Text color={'foraBlue'} marginLeft={5}>
                      {currentYear.year - 1}
                    </Text>
                  </Button>
                )}
              </Box>

              <Headings.H5 marginBottom={20} style={{ justifySelf: 'center' }}>
                {currentYear.year}
              </Headings.H5>

              <Box style={{ justifySelf: 'right' }}>
                {currentIndex !== 0 && (
                  <Button styleAs="link" onClick={handleNextClick}>
                    <Text color={'foraBlue'} marginRight={5}>
                      {currentYear.year + 1}
                    </Text>
                    <Assets.ChevronIcon direction="right" variant="thin" color={'foraBlue'} />
                  </Button>
                )}
              </Box>
            </CssGrid>

            <CssGrid
              gridTemplateColumns={['repeat(2, 1fr)', 'repeat(4, 1fr)', 'repeat(4, 1fr)']}
              columnGap={20}
              rowGap={20}
            >
              {currentYear.months.map(monthData => (
                <AttributablePayInput
                  attributablePayMonth={`${currentYear.year}${monthData.monthNo}`}
                  {...monthData}
                  key={monthData?.monthShort}
                  attrPayFormData={attrPayFormData}
                  setAttrPayFormData={setAttrPayFormData}
                  setShowTooHighAmountError={setShowTooHighAmountError}
                />
              ))}
            </CssGrid>
          </Box>
        ) : (
          <Alert variant="warning">
            <Text>{t('modal.attributablePay.noMonthsInfoText')}</Text>
          </Alert>
        )}
        {!!totalAttributableAmount && (
          <Text marginTop={20} marginBottom={20}>
            {t('modal.attributablePay.totalAttributableAmount', {
              totalAttributableAmount: swedishNumberFormatter(totalAttributableAmount),
            })}
          </Text>
        )}
        <div ref={errorMessageContainerRef}>
          <Flex flexDirection="column" rowGap={10}>
            {showTooHighAmountError && (
              <Feedback type="error">
                {t('errorMessages.validation.payroll.amountLargerThanAttributableTotal')}
              </Feedback>
            )}
          </Flex>
        </div>
        {!availableAttributablePayYears && (
          <Alert variant={'warning'}>
            <Text>{t('modal.attributablePay.noMonthsInfoText')}</Text>
          </Alert>
        )}
      </ForaModal.Content>
      <ForaModal.Footer
        onActionClick={onSaveClick}
        onCancelClick={closeModal}
        cancelButtonText={t('common.cancel')}
        actionButtonText={t('common.save')}
      />
    </>
  );
};

const AttributablePayInput = ({
  monthShort,
  attributablePayMonth,
  attrPayFormData,
  setAttrPayFormData,
  setShowTooHighAmountError,
  disabled,
}: AttributablePayInputProps) => {
  const [inputValue, setInputValue] = useState<PayInputState>(initAttrPayInput(attrPayFormData, attributablePayMonth));

  useEffect(() => {
    const attrPay = attrPayFormData.find(p => p.attributablePayMonth === attributablePayMonth);

    if (attrPay?.attributablePayAmount !== inputValue.amount) {
      setInputValue(initAttrPayInput(attrPayFormData, attributablePayMonth));
    }
    // Reinitialize the input state if the form data is changed from the parent component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attrPayFormData]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setShowTooHighAmountError(false);
    const newInputValue = inputOnChangeFormatPay(e.target.value);
    setInputValue(newInputValue);

    const newAttrPayFormData = attrPayFormData.filter(p => p.attributablePayMonth !== attributablePayMonth);

    if (newInputValue.amount === null && newAttrPayFormData.length === attrPayFormData.length) {
      setInputValue({ amount: null, formatted: '' });
      return;
    }

    if (newInputValue.amount !== null) {
      newAttrPayFormData.push({ attributablePayMonth, attributablePayAmount: newInputValue.amount });
    }

    setAttrPayFormData(newAttrPayFormData);
  };

  return (
    <Box>
      <InputField
        value={inputValue.formatted}
        id={attributablePayMonth}
        label={monthShort || ''}
        onChange={handleChange}
        errors={[]}
        isActive={!!inputValue.amount}
        labelColor="black80"
        disabled={disabled}
      />
    </Box>
  );
};
